/**
 * What can be typed into the bar, shown when the search is just "?".
 *
 * Every key in this organisation's words, with an example that can be
 * clicked to try it.
 */
import { Code, Stack, Text, UnstyledButton } from '@mantine/core';
import { TOKENS } from '../../theme';
import type { Vocab } from '../../vocab';
import { KEY_NAMES, type KeyId } from './query';
import { keyDescription, keyName } from './suggest';

type Row = { key: KeyId; example: string };

/** What free text is matched against, as the server matches it. */
export const FREE_TEXT_HELP = 'Anything else matches name, email, phone, company, postcode or UPRN. To search just one of them, use first:, last:, name:, email:, phone: or company:.';

function ExampleLink({ ex, onExample }: { ex: string; onExample: (example: string) => void }) {
  return (
    <UnstyledButton
      component="span"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onExample(ex)}
      aria-label={`Try ${ex}`}
      style={{ fontSize: 12, color: TOKENS.textSecondary, fontFamily: 'var(--mantine-font-family-monospace)', textDecoration: 'underline dotted' }}
    >
      {ex}
    </UnstyledButton>
  );
}

export function helpRows(people = true, ownership = true, scoring = true): Row[] {
  const k = keyName;
  const rows: Row[] = [
    { key: 'campaign', example: `${k('campaign')}:"Energy Premium"` },
    { key: 'supplier', example: `${k('supplier')}:acme` },
    { key: 'buyer', example: `${k('buyer')}:northwind` },
    { key: 'from', example: 'from:energy premium' },
    { key: 'status', example: 'status:sold' },
    { key: 'date', example: 'date:last7' },
    { key: 'after', example: 'after:2026-10-01' },
    { key: 'before', example: 'before:2026-10-14' },
    { key: 'value', example: 'value:>5' },
    { key: 'source', example: `${k('source')}:web_form` },
    { key: 'has', example: 'has:uprn' },
    { key: 'repeats', example: 'repeats:>1' },
    { key: 'owner', example: 'owner:me' },
    { key: 'score', example: 'score:>50' },
    { key: 'band', example: 'band:hot' },
    { key: 'first', example: 'first:=sam' },
    { key: 'last', example: 'last:smi*' },
    { key: 'name', example: 'name:"sam smith"' },
    { key: 'email', example: 'email:gmail' },
    { key: 'phone', example: 'phone:07700' },
    { key: 'company', example: 'company:acme' },
    { key: 'custom', example: 'custom.boiler_age:>10' },
  ];
  return rows.filter((r) => {
    if (r.key === 'repeats') return people;
    if (r.key === 'owner') return people && ownership;
    if (r.key === 'score' || r.key === 'band') return scoring;
    return true;
  });
}

/** The other spellings a key accepts, for the help panel. */
function otherNames(id: KeyId): string[] {
  if (id === 'custom') return [];
  const shown = keyName(id);
  return KEY_NAMES[id].filter((n) => n !== shown);
}

export function HelpPanel({
  v, onExample, people = true, ownership = true, scoring = true,
}: { v: Vocab; onExample: (example: string) => void; people?: boolean; ownership?: boolean; scoring?: boolean }) {
  return (
    <div data-testid="cmdk-help" style={{ padding: '6px 16px 12px' }}>
      <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={6} style={{ letterSpacing: '0.06em' }}>
        Search filters
      </Text>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(96px, auto) 1fr auto', gap: '6px 14px', alignItems: 'baseline' }}>
        {helpRows(people, ownership, scoring).map((r) => {
          const alt = otherNames(r.key);
          return (
            <div key={r.key} style={{ display: 'contents' }}>
              <Code style={{ background: TOKENS.surface2, color: TOKENS.text, justifySelf: 'start' }}>
                {r.key === 'custom' ? 'custom.<field>:' : `${keyName(r.key as Exclude<KeyId, 'custom'>)}:`}
              </Code>
              <Text size="sm">
                {keyDescription(r.key, v)}
                {alt.length > 0 && <Text span size="xs" c="dimmed">{`  also ${alt.map((a) => `${a}:`).join(' ')}`}</Text>}
              </Text>
              <UnstyledButton
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onExample(r.example)}
                aria-label={`Try ${r.example}`}
                style={{ fontSize: 12, color: TOKENS.textSecondary, fontFamily: 'var(--mantine-font-family-monospace)', textAlign: 'right' }}
              >
                {r.example}
              </UnstyledButton>
            </div>
          );
        })}
      </div>
      <Stack gap={4} mt={12}>
        <Text size="xs" c="dimmed">
          {`Statuses: delivered, ${v.p.sold === 'sold' ? 'sold' : 'sold (or accepted)'}, unsold, rejected, pending, filtered, duplicate, error, suppressed, returned.`}
        </Text>
        <Text size="xs" c="dimmed">
          Dates: today, yesterday, last7, last30, last90, thisweek, thismonth, lastmonth, a day such as 2026-10-01, a month such as oct, or a range such as 2026-10-01..2026-10-14. Days are UK days.
        </Text>
        <Text size="xs" c="dimmed">
          {`Put - in front of a filter to leave those out, for example -status:rejected. Names with spaces can be quoted, or just typed: from:energy premium works. from: with a date means from that day; with a name it means that ${v.one('campaign')} or ${v.one('supplier')}.`}
        </Text>
        <Text size="xs" c="dimmed">
          {`${keyName('source')}: is always the ${v.p.sourceTagLower}, and ${keyName('supplier')}: is always the ${v.one('supplier')} that sent it, in every organisation.`}
        </Text>
        <Text size="xs" c="dimmed">
          Put OR between filters to match either:{' '}
          <ExampleLink ex="status:rejected OR value:<5" onExample={onExample} />.
          OR joins the filters either side of it. Use brackets to group more:{' '}
          <ExampleLink ex={`${keyName('campaign')}:broadband (status:sold OR value:>10)`} onExample={onExample} />.
        </Text>
        <Text size="xs" c="dimmed">
          {FREE_TEXT_HELP}
        </Text>
      </Stack>
    </div>
  );
}
